import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PrimaryButton } from 'src/editor/shared/ui-lib/buttons/button'
import { AutomationIcon } from 'src/editor/shared/icons/automations-icon'
import { AddActionsList } from './add-actions-list'
import { AutomationRuleActionsEnum } from 'src/editor/shared/types/automation-rules/actions-types'
import { AutomationRuleTriggersEnum } from 'src/editor/shared/types/automation-rules/triggers-types'
import { automationRuleActionsTitlesLocaleKeys } from './constants/loco-keys'
import { SettingsNestedMenuScreen } from 'src/editor/layout/sidebar/settings/settings-nested-menu/settings-nested-menu-screen'
import { TriggerKeyType } from 'src/editor/shared/types/automation-rules/triggers-types'
import { ActionIdType } from 'src/editor/shared/types/automation-rules/actions-types'
import { useAutomationRules } from 'client/store'
import automationRulesSelectors from 'client/store/automationRules/automationRulesSelectors'
import { FullAddedActionsList } from './full-edited-actions-list'
import { EditedActionsPreview } from './edited-actions-preview'
import { AddOrEditAction } from './add-or-edit-action'
import { SidebarSecondLevel } from 'src/editor/layout/sidebar/sidebar-second-level'

interface AutomationRulesSettingsProps {
  entityId: string
  triggerKey: TriggerKeyType
  triggerType: AutomationRuleTriggersEnum
}

export const AutomationRulesSettings = ({
  entityId,
  triggerKey,
  triggerType,
}: AutomationRulesSettingsProps) => {
  const { t } = useTranslation()

  const [openedAddAction, setOpenedAddAction] = useState<
    AutomationRuleActionsEnum | undefined
  >()
  const [openedEditActionId, setOpenedEditActionId] = useState<
    ActionIdType | undefined
  >()
  const [isAddActionsOpened, setIsAddActionsOpened] = useState(false)
  const [isFullEditedActionsOpened, setIsFullEditedActionsOpened] =
    useState(false)

  const editedActions = useAutomationRules(
    automationRulesSelectors.getAutomationRuleActionsByEntityId(
      entityId,
      triggerKey,
    ),
  )

  const openedEditAction = useAutomationRules(
    automationRulesSelectors.getAutomationRuleActionByEntityId(
      entityId,
      triggerKey,
      openedEditActionId,
    ),
  )

  return (
    <div className="flex flex-col items-stretch gap-1.5">
      <EditedActionsPreview
        actions={editedActions}
        onOpenEditAction={setOpenedEditActionId}
        onOpenFullEditedActionsList={() => setIsFullEditedActionsOpened(true)}
      />
      {isFullEditedActionsOpened && (
        <SettingsNestedMenuScreen
          title={t(
            'entity_settings.automation_rules_settings.added_actions.label',
          )}
          onClose={() => {
            setIsFullEditedActionsOpened(false)
          }}
        >
          <FullAddedActionsList
            actions={editedActions}
            onOpenEditAction={id => {
              setIsAddActionsOpened(false)
              setOpenedAddAction(undefined)
              setOpenedEditActionId(id)
            }}
          />
          <div className="mt-4"></div>
          <PrimaryButton
            className="w-full"
            iconBefore={<AutomationIcon />}
            onClick={() => {
              setOpenedEditActionId(undefined)
              setOpenedAddAction(undefined)
              setIsAddActionsOpened(true)
            }}
          >
            {t('entity_settings.automation_rules_settings.add_action_button')}
          </PrimaryButton>
        </SettingsNestedMenuScreen>
      )}
      <PrimaryButton
        iconBefore={<AutomationIcon />}
        onClick={() => {
          setOpenedEditActionId(undefined)
          setOpenedAddAction(undefined)
          setIsAddActionsOpened(true)
        }}
      >
        {t('entity_settings.automation_rules_settings.add_action_button')}
      </PrimaryButton>
      <SidebarSecondLevel
        isOpened={isAddActionsOpened}
        title={t('entity_settings.automation_rules_settings.add_action')}
        onClose={() => {
          setIsAddActionsOpened(false)
        }}
      >
        <AddActionsList
          onOpenAddAction={type => {
            setOpenedEditActionId(undefined)
            setOpenedAddAction(type)
          }}
        />
      </SidebarSecondLevel>

      <SidebarSecondLevel
        isOpened={!!openedAddAction || !!openedEditActionId}
        title={
          openedAddAction
            ? t(automationRuleActionsTitlesLocaleKeys[openedAddAction])
            : openedEditAction
              ? t(automationRuleActionsTitlesLocaleKeys[openedEditAction.type])
              : ''
        }
        onClose={() => {
          setOpenedEditActionId(undefined)
          setOpenedAddAction(undefined)
        }}
      >
        <AddOrEditAction
          actionId={openedEditActionId}
          actionType={openedAddAction}
          entityId={entityId}
          triggerType={triggerType}
          triggerKey={triggerKey}
          onClose={() => {
            setOpenedEditActionId(undefined)
            setOpenedAddAction(undefined)
          }}
        />
      </SidebarSecondLevel>
    </div>
  )
}
